import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  enums,
  OptimizelyProvider,
  createInstance,
} from '@optimizely/react-sdk';

import OptimizelyManager from './optimizely-manager';
import {
  dataFileTimeout,
  optimizelyUserAttributesSelector,
  optimizelyUserId,
} from 'shared/app/state/selectors/optimizely';
import { trackEvent } from 'shared/app/utils/uo-tracking';
import isBrowser from 'shared/app/shell/utils/is-browser';
import { forceOptimizelyVariations } from './force-optly-variations';
// Report experiment impression event to UO for `mParticle` integration. `type` can be
// `flag` or `feature`.
const trackImpressionEvent = ({ userId, attributes, decisionInfo, type }) => {
  const decisionAttributes =
    type === 'feature'
      ? {
          'decisionInfo.featureKey': decisionInfo.featureKey,
          'decisionInfo.featureEnabled': decisionInfo.featureEnabled,
        }
      : {
          'decisionInfo.flagKey': decisionInfo.flagKey,
          'decisionInfo.variationKey': decisionInfo.variationKey,
        };

  const optimizelyAttributes = {
    optimizelyUserId: userId,
    optimizelyType: type,
    ...attributes,
    ...decisionAttributes,
  };

  trackEvent('OPTIMIZELY_RUN', optimizelyAttributes);
};

const Provider = ({ children }) => {
  const userAttributes = useSelector(optimizelyUserAttributesSelector);

  const optimizelyManager = useMemo(
    () => new OptimizelyManager(createInstance),
    []
  );
  const optimizelyInstance = useMemo(
    () => optimizelyManager.getInstance(),
    [optimizelyManager]
  );

  useEffect(() => {
    const { DECISION } = enums.NOTIFICATION_TYPES;

    optimizelyInstance.onReady({ timeout: dataFileTimeout }).then(() => {
      optimizelyInstance.notificationCenter.addNotificationListener(
        DECISION,
        trackImpressionEvent
      );

      forceOptimizelyVariations(optimizelyInstance);
    });

    return function cleanup() {
      optimizelyInstance.notificationCenter.clearNotificationListeners(
        DECISION
      );
    };
  }, []);

  return (
    <OptimizelyProvider
      isServerSide={!isBrowser}
      optimizely={optimizelyInstance}
      timeout={dataFileTimeout}
      user={{
        id: optimizelyUserId,
        attributes: userAttributes,
      }}
    >
      {children}
    </OptimizelyProvider>
  );
};

export default Provider;
